import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldMessageComponent } from '../field-message/field-message.component';

/**
 * Textarea form control with validation messaging and character counting.
 */
@Component({
  selector: 'app-field-textarea',
  imports: [FieldMessageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldTextareaComponent),
      multi: true,
    },
  ],
  templateUrl: './field-textarea.component.html',
  styleUrl: './field-textarea.component.css',
})
export class FieldTextareaComponent implements ControlValueAccessor {
  /** Field label. A trailing `*` marks the field as required. */
  readonly label        = input('');
  /** Native id assigned to the textarea element. */
  readonly id           = input('');
  /** Placeholder text shown when the value is empty. */
  readonly placeholder  = input('');
  /** Visible textarea row count. */
  readonly rows         = input(3);
  /** Optional character limit. */
  readonly maxLength    = input<number | null>(null);
  /** CSS resize behavior for the textarea. */
  readonly resize       = input<'none' | 'vertical' | 'horizontal' | 'both'>('vertical');
  /** Whether the field is currently invalid. */
  readonly invalid      = input(false);
  /** Validation error text. */
  readonly errorMessage = input('');
  /** Optional helper text shown below the control. */
  readonly hint         = input('');

  /** Current form value. */
  readonly value      = signal('');
  /** Disabled state received from Angular forms. */
  readonly isDisabled = signal(false);
  /** Current character count. */
  readonly charCount  = computed(() => this.value().length);

  /** Registered Angular forms change callback. */
  private onChangeFn:  (v: string) => void = () => {};
  /** Registered Angular forms touched callback. */
  private onTouchedFn: () => void          = () => {};

  /** @inheritdoc */
  writeValue(v: string): void              { this.value.set(v ?? ''); }
  /** @inheritdoc */
  registerOnChange(fn: (v: string) => void): void { this.onChangeFn = fn; }
  /** @inheritdoc */
  registerOnTouched(fn: () => void): void         { this.onTouchedFn = fn; }
  /** @inheritdoc */
  setDisabledState(d: boolean): void              { this.isDisabled.set(d); }

  /**
   * Handles native textarea changes and forwards them to Angular forms.
   *
   * @param event Native input event from the textarea.
   */
  onInput(event: Event): void {
    const val = (event.target as HTMLTextAreaElement).value;
    this.value.set(val);
    this.onChangeFn(val);
    this.onTouchedFn();
  }

  /**
   * Marks the control as touched when focus leaves the textarea.
   */
  onBlur(): void { this.onTouchedFn(); }
}
